import { useState } from "react";
import { Box, Button, Theme, Typography, useMediaQuery } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  LoadingButton,
  Modal,
  ScrollableWithGradients,
} from "@rentablo/components";
import { useCreateSupportTicket, useUsersMyself } from "@rentablo/api/apiv1";
import { AttachmentsField } from "./components/AttachmentsField";
import { SupportTicketFields } from "./components/SupportTicketFields";
import { buildSummary, fileToAttachment } from "./helpers";
import { SupportTicketFormValues } from "./types";
import {
  MAX_ATTACHMENTS,
  MAX_BANK_LENGTH,
  MAX_ERROR_CODE_OR_URL_LENGTH,
  MAX_ISIN_LENGTH,
  MAX_STEPS_TO_REPRODUCE_LENGTH,
} from "./constants";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const SupportTicketModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  // Matches the modal's own horizontal padding, so the scrollbar sits on its edge.
  const offsetValue = isSM ? 6 : 5;

  const [files, setFiles] = useState<File[]>([]);
  const [isEncoding, setIsEncoding] = useState(false);

  const { data: myself } = useUsersMyself();
  const { mutate: createTicket, isPending } = useCreateSupportTicket();

  const maxLength = ({ max }: { max: number }) => t("form.errors.max", { max });

  const schema = Yup.object().shape({
    bank: Yup.string().max(MAX_BANK_LENGTH, maxLength),
    isin: Yup.string().max(MAX_ISIN_LENGTH, maxLength),
    stepsToReproduce: Yup.string()
      .trim()
      .required(t("customerSupport.stepsToReproduce.required"))
      .max(MAX_STEPS_TO_REPRODUCE_LENGTH, maxLength)
      .defined(),
    errorCodeOrUrl: Yup.string().max(MAX_ERROR_CODE_OR_URL_LENGTH, maxLength),
  });

  const { control, handleSubmit } = useForm<SupportTicketFormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      bank: "",
      isin: "",
      stepsToReproduce: "",
      errorCodeOrUrl: "",
    },
  });

  const stepsToReproduce = useWatch({ control, name: "stepsToReproduce" });

  const showError = (message: string) =>
    enqueueSnackbar(message, { variant: "error", showCloseButton: true });

  const onSubmit = async (data: SupportTicketFormValues) => {
    setIsEncoding(true);

    let attachments;
    try {
      attachments = await Promise.all(files.map(fileToAttachment));
    } catch {
      setIsEncoding(false);
      showError(t("customerSupport.attachments.readError"));
      return;
    }
    setIsEncoding(false);

    createTicket(
      {
        createSupportTicketParams: {
          summary: buildSummary(data.stepsToReproduce),
          bank: data.bank?.trim() || undefined,
          isin: data.isin?.trim() || undefined,
          stepsToReproduce: data.stepsToReproduce.trim(),
          errorCodeOrUrl: data.errorCodeOrUrl?.trim() || undefined,
          attachments: attachments.length ? attachments : undefined,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t("customerSupport.success"), {
            variant: "success",
            showCloseButton: true,
          });
          onClose();
        },
        onError: () => showError(t("customerSupport.error")),
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t("customerSupport.title")}
      subtitle={t("customerSupport.subtitle")}
      width={700}
      height="auto"
      maxHeight="calc(100% - 24px)"
      position={isSM ? "bottom" : "centered"}
      onClose={onClose}
    >
      <ScrollableWithGradients
        flex={1}
        minHeight={0}
        sx={{ mr: -offsetValue, pr: offsetValue }}
      >
        <Box display="flex" flexDirection="column" gap={6}>
          <Typography variant="h6" component="span">
            {t("customerSupport.userId.label")} {myself?.id ?? "—"}
          </Typography>
          <SupportTicketFields control={control} />
          <AttachmentsField
            files={files}
            onChange={setFiles}
            onReject={() =>
              showError(
                t("customerSupport.attachments.rejected", {
                  max: MAX_ATTACHMENTS,
                })
              )
            }
          />
        </Box>
      </ScrollableWithGradients>
      <Box
        display="flex"
        flexDirection={isSM ? "column-reverse" : "row"}
        justifyContent="space-between"
        gap={4}
        pt={6}
        flexShrink={0}
      >
        <Button variant="tertiary" onClick={onClose}>
          {t("customerSupport.cancel")}
        </Button>
        <LoadingButton
          isFetching={isPending || isEncoding}
          variant="primary"
          disabled={!stepsToReproduce.trim()}
          onClick={handleSubmit(onSubmit)}
        >
          {t("customerSupport.submit")}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
